import { AppEnvironment } from 'environments/environment';
import { getRootDomain, isCapacitorNative } from 'environments/environment-helpers';
import { AppVersion } from 'app/version';
import { BackendRoute, decideNativeRoute } from 'util/backend-routing';
import { BackendRouteGate } from 'util/backend-route-gate';

// Which server origin a request goes to: the native prod bundle's runtime
// backend routing, and the one origin that is safe to share outside the device.
//
// These live here rather than in environments/environment-helpers.ts because
// they read AppEnvironment, and that module must not: the Firebase config files
// import it, and environments/environment imports those, so a read of
// AppEnvironment there closed an import cycle. This module sits outside that
// cycle. Nothing in the cycle imports it.

// ---------------------------------------------------------------------------
// Native prod-bundle backend routing (#4280, reshaped by #4405).
//
// A native prod build talks either to the live/apex server (mh-physique.app)
// or to the canary/staging server (canary.mh-physique.app). The choice is made
// at runtime from ONE signal: is this build NEWER than what is live in
// production? A pre-release build (TestFlight / Play internal / a local device
// deploy / App Review) is by definition newer than the live release, so it
// routes to canary; the live build and anything older route to apex. This
// fixes #4273 (a native build ahead of live was pinned to live, whose older
// server rejected its new-format credentials — a silent lockout) and closes
// the "can't exercise canary against the native app" testing gap.
//
// The native side (BackendRouteResolver.java on Android, MainViewController.swift
// on iOS — a plain helper, NOT a Capacitor plugin) fetches the live version from
// the always-live production host, caches it, and injects it as the global
// `window.__CS_BACKEND_LIVE_VERSION__`. On every boot after the first it has a
// cached value and injects it before the page loads, so the answer is there
// from the first line of JS. On the first-ever boot it has nothing to inject
// yet: the page loads anyway, the probe runs in the background, and the value
// is injected the moment it answers.
//
// #4405 — WHY THE PAGE NO LONGER WAITS. #4280 made the first-ever boot wait for
// the probe by holding the page back natively, so this file could answer
// synchronously and throw if it could not. That hold cost us a boot crash on
// Android (#4404, shipped in three canaries) and left an offline first boot on a
// blank screen forever with no message and no way out, because no web code was
// running to draw one. So the hold is gone, and the two accessors below take its
// place. See design-docs/backend-routing.md.
//
// THE TWO KINDS OF CALLER. Splitting them is the whole idea, so pick the right
// one deliberately:
//
//   getApiOriginAsync()          — for a request whose ANSWER DEPENDS ON WHICH
//                                  SERVER handles it: anything under /api/<function>
//                                  and the /api/search route. It waits for the
//                                  real route and never guesses. Reaching the
//                                  wrong server here is the #4273 lockout class.
//
//   getUnversionedRelayOrigin()  — for a request that behaves the same on either
//                                  server and carries no account data: the Sentry
//                                  tunnel, the Mixpanel relay, the connectivity
//                                  ping. It answers immediately, using the real
//                                  route when it is already known and the live
//                                  host while it is not.
//
// The final apex-vs-canary decision runs HERE, in the pure, unit-tested core
// decideNativeRoute(), using this build's own git-describe AppVersion — which
// carries the commit distance the tie-break needs (a build made from commits
// ahead of live's tag WITHOUT a tag bump must still route to canary; the native
// version strings do not carry that distance, so the decision cannot move into
// native).
//
// MIRRORED CONSTANTS: these three global names are also written / called by the
// native side (BackendRouteResolver.java and MainViewController.swift). Keep the
// three files in step.
const BACKEND_LIVE_VERSION_GLOBAL = '__CS_BACKEND_LIVE_VERSION__';
// native-provided provenance of the injected value, for telemetry only
// ('cache' | 'probed'); never drives routing.
const BACKEND_ROUTE_SOURCE_GLOBAL = '__CS_BACKEND_ROUTE_SOURCE__';
// #4405: the callback native invokes AFTER setting the two globals above, to
// wake a page that loaded before its probe answered. Only published on a
// first-ever boot — a cached boot has the value already and installs nothing.
const BACKEND_ROUTE_NOTIFY_GLOBAL = '__CS_BACKEND_ROUTE_NOTIFY__';

// How long a route-sensitive caller waits for the native probe before giving up
// (#4405). The only reason the answer is late is that the network has not
// responded, so the failure is classified as a network error and the app's
// normal offline handling takes over.
//
// 15s is chosen to cover roughly the native probe's first three attempts: each
// attempt self-bounds at 4s and the backoff between them is 0.5s, 1s, 2s. It is
// far below callApiAsync's own 80s request budget, so a genuinely offline first
// boot reports "cannot reach the server" in a reasonable time instead of sitting
// there — which is the frozen-screen behaviour this ticket removes.
export const BACKEND_ROUTE_WAIT_MS = 15_000;

// only the prod bundle chooses a backend at runtime; the dev/local native
// bundles keep their fixed configured backend and never read the injected
// global (native only injects it for the prod flavor)
function isNativeProdBundle(): boolean {
  return isCapacitorNative() && AppEnvironment.name === 'prod';
}

// read a string global off window (undefined when missing, empty, or non-string)
function readInjectedString(name: string): string | undefined {
  const raw = (window as unknown as Record<string, unknown>)[name];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

// The session's route gate, built on first use.
//
// Only the native prod bundle has a route to resolve, so nothing is built for
// web or for the dev/local native bundles; their origin is fixed and the
// accessors below return it without ever touching the gate. `null` records
// "already decided there is no gate", so the check runs once either way.
let backendRouteGate: BackendRouteGate | null | undefined = undefined;

// Built lazily, on the first call rather than at module load. (This used to be
// required: the code lived in environment-helpers.ts, which sat inside an import
// cycle with environments/environment. That cycle is gone, but building on first
// use still means web and dev bundles never touch the gate at all.)
//
// Building late is safe because of the race contract on BackendRouteGate: it
// reads the injected global before installing its notify hook, so a value that
// native published before the first call is picked up at construction, and one
// published afterwards arrives through the hook. There is no window in which an
// answer is dropped, whenever the first call happens to be.
function getBackendRouteGate(): BackendRouteGate | null {
  if (backendRouteGate === undefined) {
    backendRouteGate = isNativeProdBundle()
      ? new BackendRouteGate(
          () => readInjectedString(BACKEND_LIVE_VERSION_GLOBAL),
          (notify) => { (window as unknown as Record<string, unknown>)[BACKEND_ROUTE_NOTIFY_GLOBAL] = notify; },
        )
      : null;
  }
  return backendRouteGate;
}

// the backend for everything that is NOT the native prod bundle: web uses
// whatever URL the user has open, and the dev/local native bundles use their
// fixed configured host
function getFixedOrigin(): string {
  if (!isCapacitorNative()) return location.origin;
  return `https://${AppEnvironment.firebaseConfig.authDomain}`;
}

// map a resolved live version to the concrete host for this build
function originForLiveVersion(liveVersion: string): string {
  if (decideNativeRoute(AppVersion, liveVersion) === 'canary') {
    return `https://canary.${getRootDomain()}`;
  }
  return `https://${AppEnvironment.firebaseConfig.authDomain}`;
}

/**
 * The API origin for a request whose answer depends on WHICH SERVER handles it.
 *
 * Use this for every call under `/api/` that runs real server logic — the
 * declared functions in cloud-functions.service.ts and the food search in
 * food-db.service.ts. On Capacitor native, location.origin is https://localhost,
 * so these calls need a real server origin.
 *
 * On the native prod bundle this WAITS for the route rather than guessing one.
 * Guessing is the #4273 lockout class: a build newer than live, pinned to the
 * live server, whose older code could not read its credentials — the user was
 * locked out of their own account with no explanation. The wait is bounded by
 * BACKEND_ROUTE_WAIT_MS and then fails as a network error; it never invents a
 * host. On web and on the dev/local native bundles it resolves immediately.
 */
export async function getApiOriginAsync(): Promise<string> {
  const gate = getBackendRouteGate();
  if (!gate) return getFixedOrigin();
  return originForLiveVersion(await gate.whenResolvedAsync(BACKEND_ROUTE_WAIT_MS));
}

/**
 * The origin for a request that behaves IDENTICALLY on either server and
 * carries no account data. Answers immediately and never waits.
 *
 * Today's callers, and why each qualifies:
 *  - the Sentry tunnel and the bootstrap-failure beacon (`/api/e`) — a pure
 *    relay to whatever Sentry ingest host the envelope's own DSN names, so both
 *    servers deliver the event to the same Sentry project;
 *  - the Mixpanel relay (`/api/t`) — a pure passthrough to the Mixpanel host in
 *    server config, which is the same project behind both servers;
 *  - the connectivity ping — fetches a static image purely to see whether the
 *    network answers at all.
 *
 * ADMISSION TEST for any new caller, and it is a real test, not a formality:
 * the endpoint must produce the same result on the live and canary servers, AND
 * the request must carry no account state. If either is in doubt, use
 * getApiOriginAsync() instead. Getting this wrong is silent — the request
 * succeeds against the wrong server and nothing reports it.
 *
 * While the route is still unknown (only ever the first moments of a first-ever
 * native boot) this returns the live/apex host. That is safe by the same test
 * above, and it is what lets the app report "no connection" on an offline first
 * boot instead of waiting on the very probe that cannot answer.
 */
export function getUnversionedRelayOrigin(): string {
  const gate = getBackendRouteGate();
  if (!gate) return getFixedOrigin();
  const liveVersion = gate.peekLiveVersion();
  if (liveVersion === undefined) return `https://${getRootDomain()}`;
  return originForLiveVersion(liveVersion);
}

// A routing snapshot for telemetry (NOT for routing — the accessors above own
// that). Tolerant: never throws and never waits, so a boot-telemetry call can't
// brick boot. Returns undefined off the native prod bundle.
//
// provenance of the injected route value, for telemetry ('cache' = read from
// device storage on a later boot; 'probed' = fetched fresh on the first-ever
// boot; 'pending' = the probe has not answered yet, which #4405 made a normal
// state rather than a bug; 'unknown' = the source global was present but not one
// of the values native writes). A string-literal union, not an enum, on purpose:
// 'cache'/'probed' are the literal strings the native side injects and this
// becomes the `backend_route_source` Sentry tag verbatim, so a mapping layer
// would only add drift risk.
export type BackendRouteSource = 'cache' | 'probed' | 'pending' | 'unknown';
export interface BackendRouteInfo {
  /** undefined while the route is still unresolved — callers must not read that as apex */
  route: BackendRoute | undefined;
  clientVersion: string;
  liveVersion: string | undefined;
  source: BackendRouteSource;
}
export function getBackendRouteInfo(): BackendRouteInfo | undefined {
  const gate = getBackendRouteGate();
  if (!gate) return undefined; // not the native prod bundle — nothing routed at runtime
  const liveVersion = gate.peekLiveVersion();
  // report "not known yet" as undefined rather than defaulting to apex. The old
  // tolerant apex default was harmless when native guaranteed the value was
  // always there; now that an unresolved first boot is normal, defaulting would
  // publish a route this session may never actually use.
  const route = liveVersion === undefined ? undefined : decideNativeRoute(AppVersion, liveVersion);
  const rawSource = readInjectedString(BACKEND_ROUTE_SOURCE_GLOBAL);
  const source: BackendRouteSource =
    rawSource === 'cache' || rawSource === 'probed' ? rawSource
      : liveVersion === undefined ? 'pending'
        : 'unknown';
  return { route, clientVersion: AppVersion, liveVersion, source };
}

/** Waits for the route to resolve, so telemetry reports what this session
 *  actually used. Resolves to undefined off the native prod bundle, and also
 *  when the route never resolves — telemetry must never block or throw. */
export async function whenBackendRouteSettledAsync(): Promise<BackendRouteInfo | undefined> {
  const gate = getBackendRouteGate();
  if (!gate) return undefined;
  try {
    await gate.whenResolvedAsync(BACKEND_ROUTE_WAIT_MS);
  } catch {
    // an unresolved route is itself the interesting fact here, and
    // getBackendRouteInfo() below reports it as source='pending'
  }
  return getBackendRouteInfo();
}

// Canonical per-environment URL safe to share externally. NEVER returns the canary URL
// regardless of install channel — the recipient may be on a different install channel than
// the sender (a TestFlight coach inviting an App Store client; a magic-link email opened in
// any browser). For prod env this is the apex (`mh-physique.app`); for dev env the dev
// backend (`dev.mh-physique.app`); for web (PWA) it's `location.origin` (whatever URL the
// user has open). Use this for any URL that leaves the device (emailed magic links,
// coach invite links, privacy/TOS hyperlinks opened in external browser, system-share-sheet
// URLs); use `getApiOriginAsync()` for device-local API calls that should follow the install's
// canary-vs-live routing.
//
// The prod-env branch hardcodes `getRootDomain()` (apex) rather than dereferencing
// `AppEnvironment.firebaseConfig.authDomain` so the "never canary" invariant is local to
// this function. If a future Firebase config tweak ever points the prod authDomain at a
// canary host (e.g. for Firebase Auth testing), this helper still produces apex URLs and
// the contract holds.
export function getSharedUrlOrigin(): string {
  if (!isCapacitorNative()) return location.origin;
  if (AppEnvironment.name === 'prod') return `https://${getRootDomain()}`;
  return `https://${AppEnvironment.firebaseConfig.authDomain}`;
}

